if (!customElements.get('card-media-gallery')) {
  class CardMediaGallery extends HTMLElement {
    constructor() {
      super();

      this.bindOnButtonClick = this.onButtonClick.bind(this);
      this.bindUpdateGallery = this.updateGallery.bind(this);
      this.bindOnScroll = this.onScroll.bind(this);
      this.throttledOnStopScroll = throttle(this.onStopScroll.bind(this), 16);
      this.bindOnScrollEnd = this.onScrollEnd.bind(this);
      this.bindOnMouseDown = this.onMouseDown.bind(this);
      this.bindOnMouseMove = this.onMouseMove.bind(this);
      this.bindOnMouseLeave = this.onMouseLeave.bind(this);
      this.bindOnTouchStart = this.onTouchStart.bind(this);
      this.bindOnTouchMove = this.onTouchMove.bind(this);
      this.bindOnTouchEnd = this.onTouchEnd.bind(this);

      this.resizeObserver = new ResizeObserver(entries => {
        requestAnimationFrame(() => {
          this.updateGallery();
        })
      })
    }

    connectedCallback() {
      this.observer = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting) {
          requestAnimationFrame(() => {
            this.setUp();
          });
          this.observer.unobserve(this);
        }
      }, { rootMargin: '0px 0px 200px 0px' });

      this.observer.observe(this);
    }

    setUp() {
      this.classList.remove('loaded');
      this.list = this.querySelector('.card-media-gallery__list');
      if(!this.list) return;
      this.items = Array.from(this.querySelectorAll('.card-media-gallery__item, .card-media-gallery__clone'));
      this.clones = Array.from(this.querySelectorAll('.card-media-gallery__clone'));
      this.counter = this.querySelector('.card-media-gallery__counter');
      this.images = Array.from(this.querySelectorAll('.card-media-gallery__image'));

      this.setPositions();

      this.loadImages();

      this.prevButton = this.querySelector('.card-media-gallery__button.prev');
      this.nextButton = this.querySelector('.card-media-gallery__button.next');

      this.setupBreakpointListeners();

      if (this.prevButton) this.prevButton.addEventListener('click', this.bindOnButtonClick);
      if (this.nextButton) this.nextButton.addEventListener('click', this.bindOnButtonClick);

      this.resizeObserver.observe(this)
      this.list.addEventListener('scroll', this.bindOnScroll);
      this.list.addEventListener('scroll', this.throttledOnStopScroll);
      this.list.addEventListener('scrollend', this.bindOnScrollEnd);

      this.isDragging = false;
      this.hasMoved = false;
      this.hasInteracted = false;
      this.startPos = 0;
      this.startPosY = 0;
      this.isRepositioning = false;
      this.isAnimating = false;
      this.updateMouseDragBinding();
      this.list.addEventListener('touchstart', this.bindOnTouchStart, { passive: false });

      this.scrollToFirstItem();
    }

    disconnectedCallback() {
      this.resizeObserver.disconnect();
      if (this.observer) {
        this.observer.disconnect();
      }
      if (this.boundUpdateMouseDragBinding) {
        if (this.tabletQuery) this.tabletQuery.removeEventListener('change', this.boundUpdateMouseDragBinding);
        if (this.desktopQuery) this.desktopQuery.removeEventListener('change', this.boundUpdateMouseDragBinding);
      }
      if (this.list) this.list.removeEventListener('mousedown', this.bindOnMouseDown);
    }

    getCurrentBreakpoint() {
      if (this.desktopQuery ? this.desktopQuery.matches : window.innerWidth >= 990) return 'desktop';
      if (this.tabletQuery ? this.tabletQuery.matches : window.innerWidth >= 750) return 'tablet';
      return 'mobile';
    }

    isMobile() {
      return this.getCurrentBreakpoint() === 'mobile';
    }

    isInsideSlider() {
      return this.closest('component-slider') !== null;
    }

    isForcedGridAtCurrentBreakpoint() {
      const slider = this.closest('component-slider');
      if (!slider) return true;
      return slider.classList.contains(`forced-grid-${this.getCurrentBreakpoint()}`);
    }

    shouldBlockMouseDrag() {
      return this.isInsideSlider() && !this.isForcedGridAtCurrentBreakpoint();
    }

    setupBreakpointListeners() {
      this.tabletQuery = window.matchMedia('(min-width: 750px)');
      this.desktopQuery = window.matchMedia('(min-width: 990px)');
      this.boundUpdateMouseDragBinding = this.updateMouseDragBinding.bind(this);
      this.tabletQuery.addEventListener('change', this.boundUpdateMouseDragBinding);
      this.desktopQuery.addEventListener('change', this.boundUpdateMouseDragBinding);
    }

    updateMouseDragBinding() {
      if (!this.list) return;

      if (this.shouldBlockMouseDrag()) {
        this.list.removeEventListener('mousedown', this.bindOnMouseDown);
      } else {
        this.list.addEventListener('mousedown', this.bindOnMouseDown, { passive: false });
      }
    }

    getDefaultSlideIndex() {
      return (!this.isMobile() && this.hasAttribute('data-start-second-image-desktop')) ? 1 : 0;
    }

    scrollToItemIndex(index, behavior = 'instant') {
      const item = this.items.find((item) => +item.dataset.index === index);
      if (!item) return;
      this.list.scroll({
        left: +item.dataset.position,
        behavior
      })
    }

    scrollToFirstItem() {
      this.scrollToItemIndex(this.getDefaultSlideIndex());
      this.classList.add('loaded');
    }

    setPositions() {
      this.positions = [];
      this.items.forEach((item) => {
        const position = item.offsetLeft - this.offsetLeft;
        item.dataset.position = position;
        this.positions.push(position);
      });

      this.clones.forEach((clone) => {
        const relItem = this.querySelector(`#${clone.dataset.item}`);
        if (relItem) clone.dataset.relPosition = relItem.dataset.position;
      })
    }

    onButtonClick(event) {
      this.hasInteracted = true;
      const currentScroll = this.list.scrollLeft
      if (event.currentTarget.classList.contains('prev')) {
        const positions = this.positions.filter(p => p < (currentScroll - 10));
        this.list.scroll({
          left: positions[positions.length - 1] || 0,
          behavior: 'smooth'
        })
      } else {
        const positions = this.positions.filter(p => p > (currentScroll + 10));
        this.list.scroll({
          left: positions[0] || this.positions[this.positions.length - 1],
          behavior: 'smooth'
        })
      }
    }

    onScroll() {
      const currentScroll = Math.round(this.list.scrollLeft)
      const scrollWidth = Math.ceil(this.list.scrollWidth - this.list.getBoundingClientRect().width)
      if (this.prevButton) {
        if(currentScroll <= 5) {
          this.prevButton.setAttribute('disabled', true);
          this.prevButton.setAttribute('aria-disabled', true);
        } else {
          this.prevButton.removeAttribute('disabled');
          this.prevButton.removeAttribute('aria-disabled');
        }
      }

      if(this.nextButton) {
        if(Math.ceil(currentScroll) >= (scrollWidth) - 5) {
          this.nextButton.setAttribute('disabled', true);
          this.nextButton.setAttribute('aria-disabled', true);
        } else {
          this.nextButton.removeAttribute('disabled');
          this.nextButton.removeAttribute('aria-disabled');
        }
      }


      if (this.counter && !this.isRepositioning) {
        const currentIndex = this.getCurrentSlideIndex();
        const totalItems = this.items.length - this.clones.length;
        const percentage = totalItems > 1 ? Math.round((currentIndex / (totalItems - 1)) * 100) : 0;
        this.counter.style.setProperty('--percentage', `${percentage}%`)
      }
    }

    getCurrentSlideIndex() {
      const currentScroll = this.list.scrollLeft;
      const realItems = this.items.filter(item => !item.classList.contains('card-media-gallery__clone'));
      const firstRealPosition = +realItems[0]?.dataset.position || 0;

      if (currentScroll < firstRealPosition - 10) {
        return realItems.length - 1;
      }

      let closestItem = null;
      let closestDistance = Infinity;

      this.items.forEach((item) => {
        const position = +item.dataset.position;
        const distance = Math.abs(currentScroll - position);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestItem = item;
        }
      });

      if (!closestItem) return 0;

      if (closestItem.classList.contains('card-media-gallery__clone')) {
        const realItemId = closestItem.dataset.item;
        const realIndex = realItems.findIndex(item => item.id === realItemId);
        return realIndex >= 0 ? realIndex : 0;
      }

      return realItems.indexOf(closestItem);
    }

    onStopScroll() {
      if (this.isRepositioning || this.isAnimating) return;

      const currentScroll = Math.round(this.list.scrollLeft)
      const firstClone = this.clones[0]
      const lastClone = this.clones[this.clones.length - 1]

      if (firstClone && currentScroll <= +firstClone.dataset.position) {
        this.isRepositioning = true;
        this.list.style.visibility = 'hidden';
        this.list.scrollLeft = +firstClone.dataset.relPosition;
        requestAnimationFrame(() => {
          this.list.style.visibility = '';
          setTimeout(() => {
            this.isRepositioning = false;
          }, 0);
        });
      }

      if (lastClone && currentScroll >= +lastClone.dataset.position) {
        this.isRepositioning = true;
        this.list.style.visibility = 'hidden';
        this.list.scrollLeft = +lastClone.dataset.relPosition;
        requestAnimationFrame(() => {
          this.list.style.visibility = '';
          setTimeout(() => {
            this.isRepositioning = false;
          }, 0);
        });
      }
    }

    onScrollEnd() {
      this.onStopScroll();
    }

    onMouseDown(event) {
      this.isDragging = true;
      this.hasMoved = false;
      this.diff = 0;
      this.list.classList.remove('touch');

      this.startPos = event.pageX - this.list.offsetLeft;
      this.initScrollLeft = this.list.scrollLeft;
      this.list.classList.add('grabbing');

      this.list.addEventListener('mousemove', this.bindOnMouseMove, { passive: false });
      this.list.addEventListener('mouseup', this.bindOnMouseLeave, { passive: false });
      this.list.addEventListener('mouseleave', this.bindOnMouseLeave, { passive: false });
      window.addEventListener('mouseleave', this.bindOnMouseLeave, { passive: false });
      window.addEventListener('mouseup', this.bindOnMouseLeave, { passive: false });
    }

    onMouseMove(event) {
      if (!this.isDragging) return;
      const x = event.pageX - this.list.offsetLeft;
      this.walk = x - this.startPos;

      this.list.classList.add('moving');
      this.hasMoved = true;
      this.hasInteracted = true;

      if(Math.abs(this.walk) < 10) return;
      event.preventDefault();

      this.list.scrollLeft = this.initScrollLeft -  this.walk;
    }

    onMouseLeave() {
      this.isDragging = false;
      this.list.classList.remove('grabbing');

      const currentScroll = Math.round(this.list.scrollLeft);
      if (this.walk > 10) {
        const positions = this.positions.filter(p => p < (currentScroll - 10));
        this.list.scroll({
          left: positions[positions.length - 1] || 0,
          behavior: 'smooth'
        })
      } else if (this.walk < -10) {
        const positions = this.positions.filter(p => p > (currentScroll + 10));
        this.list.scroll({
          left: positions[0] || this.positions[this.positions.length - 1],
          behavior: 'smooth'
        })
      }

      setTimeout(() => {
        this.list.classList.remove('moving');
      }, 300);

      this.list.removeEventListener('mousemove', this.bindOnMouseMove, { passive: false });
      this.list.removeEventListener('mouseup', this.bindOnMouseLeave, { passive: false });
      this.list.removeEventListener('mouseleave', this.bindOnMouseLeave, { passive: false });
      window.removeEventListener('mouseleave', this.bindOnMouseLeave, { passive: false });
      window.removeEventListener('mouseup', this.bindOnMouseLeave, { passive: false });
    }

    onTouchStart(event) {
      if (event.touches.length > 1) return;

      this.cancelScrollAnimation();

      this.isDragging = true;
      this.hasMoved = false;
      this.walk = 0;
      this.walkY = 0;
      this.list.classList.add('touch');

      this.startPos = event.touches[0].clientX;
      this.startPosY = event.touches[0].clientY;
      this.initScrollLeft = this.list.scrollLeft;
      this.scrollDirection = null;

      this.list.addEventListener('touchmove', this.bindOnTouchMove, { passive: false });
      this.list.addEventListener('touchend', this.bindOnTouchEnd, { passive: false });
      this.list.addEventListener('touchcancel', this.bindOnTouchEnd, { passive: false });
    }

    onTouchMove(event) {
      if (!this.isDragging || event.touches.length > 1) return;

      const x = event.touches[0].clientX;
      const y = event.touches[0].clientY;
      this.walk = x - this.startPos;
      this.walkY = y - this.startPosY;

      if (!this.scrollDirection) {
        if (Math.abs(this.walk) > 5 || Math.abs(this.walkY) > 5) {
          this.scrollDirection = Math.abs(this.walk) > Math.abs(this.walkY) ? 'horizontal' : 'vertical';
        }
      }

      if (this.scrollDirection === 'vertical') return;

      if (Math.abs(this.walk) > 10) {
        if (event.cancelable) event.preventDefault();
        this.list.classList.add('moving');
        this.hasMoved = true;
        this.hasInteracted = true;
        this.list.scrollLeft = this.initScrollLeft - this.walk;
      }
    }

    onTouchEnd() {
      if (!this.isDragging) return;

      this.isDragging = false;

      if (this.scrollDirection === 'horizontal' && this.hasMoved) {
        const currentScroll = Math.round(this.list.scrollLeft);
        let targetPosition = null;

        const realItems = this.items.filter(item => !item.classList.contains('card-media-gallery__clone'));
        const realPositions = realItems.map(item => +item.dataset.position);
        const firstRealPosition = realPositions[0];
        const lastRealPosition = realPositions[realPositions.length - 1];

        if (this.walk > 10) {
          const positions = realPositions.filter(p => p < (currentScroll - 10));
          if (positions.length > 0) {
            targetPosition = positions[positions.length - 1];
          } else {
            this.jumpToPositionWithSlide(lastRealPosition, 'back');
            targetPosition = null;
          }
        } else if (this.walk < -10) {
          const positions = realPositions.filter(p => p > (currentScroll + 10));
          if (positions.length > 0) {
            targetPosition = positions[0];
          } else {
            this.jumpToPositionWithSlide(firstRealPosition, 'forward');
            targetPosition = null;
          }
        }

        if (targetPosition !== null) {
          this.smoothScrollTo(targetPosition, 200);
        }
      }

      setTimeout(() => {
        this.list.classList.remove('moving');
        this.list.classList.remove('touch');
      }, 300);

      this.scrollDirection = null;
      this.list.removeEventListener('touchmove', this.bindOnTouchMove, { passive: false });
      this.list.removeEventListener('touchend', this.bindOnTouchEnd, { passive: false });
      this.list.removeEventListener('touchcancel', this.bindOnTouchEnd, { passive: false });
    }

    jumpToPositionWithSlide(targetPosition, direction) {
      this.isRepositioning = true;

      const itemWidth = this.items[0]?.getBoundingClientRect().width || 100;
      const slideOffset = itemWidth * 0.3;

      const startPosition = direction === 'back'
        ? targetPosition + slideOffset
        : targetPosition - slideOffset;

      this.list.style.visibility = 'hidden';
      this.list.scrollLeft = startPosition;

      requestAnimationFrame(() => {
        this.list.style.visibility = '';
        this.isRepositioning = false;
        this.smoothScrollTo(targetPosition, 150);
      });
    }

    smoothScrollTo(targetPosition, duration = 200) {
      if (this.animationId) {
        cancelAnimationFrame(this.animationId);
      }

      this.isAnimating = true;
      const startPosition = this.list.scrollLeft;
      const distance = targetPosition - startPosition;
      const startTime = performance.now();

      const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

      const animate = (currentTime) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const easedProgress = easeOutCubic(progress);

        this.list.scrollLeft = startPosition + (distance * easedProgress);

        if (progress < 1) {
          this.animationId = requestAnimationFrame(animate);
        } else {
          this.isAnimating = false;
          this.animationId = null;
          this.onStopScroll();
        }
      };

      this.animationId = requestAnimationFrame(animate);
    }

    loadImages() {
      const initItem = this.items.find((item) => +item.dataset.index === this.getDefaultSlideIndex());
      const initImage = initItem?.querySelector('.card-media-gallery__image');
      if (initImage) initImage.loading = 'eager';
    }

    cancelScrollAnimation() {
      if (this.animationId) {
        cancelAnimationFrame(this.animationId);
        this.animationId = null;
        this.isAnimating = false;
      }
    }

    updateGallery() {
      this.cancelScrollAnimation();

      this.setPositions();
      const targetIndex = this.hasInteracted ? this.getCurrentSlideIndex() : this.getDefaultSlideIndex();
      this.scrollToItemIndex(targetIndex);

      const width = this.getBoundingClientRect().width;
      const mainImage = this.closest('.media').querySelector('.card__media__featured');
      if (mainImage) mainImage.sizes = `${width}px`;
      this.images.forEach(image => {
        image.sizes = `${width}px`;
      })
    }
  }

  customElements.define('card-media-gallery', CardMediaGallery)
}
